import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import {
  ListingApplicationMailKind,
  MailDeliveryStatus,
  type ListingApplicationRowFragment,
} from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { ListingApplicationMailDialog, type MailDraft } from "./ListingApplicationMailDialog";
import { MAIL_KIND_LABELS, MAIL_PROBLEM_LABELS, MAIL_STATUS_LABELS } from "./listing-application-mail";

type Application = ListingApplicationRowFragment;
type Mail = NonNullable<Application["mails"]>[number];

/** Was nicht sicher beim Mailserver ankam, darf HR ausdrücklich erneut senden: als neue Vorschau, nie automatisch. */
const RESENDABLE = new Set<MailDeliveryStatus>([
  MailDeliveryStatus.FailedBeforeSend, MailDeliveryStatus.Cancelled, MailDeliveryStatus.DeliveryUnknown,
]);

// Den Versandstand wartender Mails lädt die Liste nach (usePendingMailRefresh), einmal für alle Zeilen.
// Die Mails einer Bewerbung und der Weg, eine zu senden
// (@external-adr GENESIS-0080-an-application-mail-sends-exactly-the-confirmed-preview-to-a-proven-address). Die
// Historie sehen Lesende der Bewerbungen; senden dürfen, wem genesis `actions.mailKinds` meldet. Der Status sagt nur,
// was der Mailserver sagte: "angenommen" ist keine Zustellung, "unklar" wird nie automatisch wiederholt. Die
// Eingangsbestätigung sendet genesis selbst; sie steht hier, sobald ihr Text geschrieben ist, und ist nie erneut
// sendbar, weil `mailKinds` sie nie meldet
// (@external-adr GENESIS-0082-a-confirmed-application-owes-one-receipt-written-outside-the-confirmation).
export function ListingApplicationMails({ application }: { application: Application }) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `share.listings.mail.${id}` }, values);
  const [dialog, setDialog] = useState<{ initial?: MailDraft; uncertain?: boolean } | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const kinds = application.actions.mailKinds;
  const mails = application.mails ?? [];
  if (!kinds.length && !mails.length) return null;

  return <div id={`listing-application-mails-${application.id}`} className="space-y-3">
    {kinds.length ? <Button id={`listing-application-mail-open-${application.id}`} size="compact" variant="secondary"
      onClick={() => setDialog({})}>{t("open")}</Button> : null}
    {mails.length ? <ul className="space-y-3" aria-label={t("history")}>
      {mails.map((mail) => <MailEntry key={mail.id} mail={mail} expanded={opened === mail.id}
        onToggle={() => setOpened(opened === mail.id ? null : mail.id)}
        onResend={kinds.includes(mail.kind) && RESENDABLE.has(mail.status) ? () => setDialog({
          initial: { kind: mail.kind, subject: mail.subject, body: mail.body },
          uncertain: mail.status === MailDeliveryStatus.DeliveryUnknown,
        }) : undefined} />)}
    </ul> : null}
    {dialog ? <ListingApplicationMailDialog applicationId={application.id} kinds={kinds} initial={dialog.initial}
      uncertainBefore={dialog.uncertain} onClose={() => setDialog(null)} /> : null}
  </div>;
}

function MailEntry({ mail, expanded, onToggle, onResend }: {
  mail: Mail; expanded: boolean; onToggle: () => void; onResend?: () => void;
}) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `share.listings.mail.${id}` }, values);
  const when = formatDateTime(mail.finishedOn ?? mail.requestedOn, intl.locale);
  return <li id={`listing-application-mail-${mail.id}`} className="text-smycles-meta">
    <p>
      <span className="font-medium text-ink">{intl.formatMessage(MAIL_KIND_LABELS[mail.kind])}</span>{" "}
      <span className="text-ink">{intl.formatMessage(MAIL_STATUS_LABELS[mail.status])}</span>
    </p>
    <p className="text-ink-muted">
      {mail.requestedBy?.formattedName ? t("sentBy", { date: when, name: mail.requestedBy.formattedName })
        : mail.kind === ListingApplicationMailKind.Receipt ? t("automatic", { date: when })
        : t("sentOn", { date: when })}
    </p>
    {mail.status === MailDeliveryStatus.SmtpAccepted ? <p className="text-ink-muted">{t("acceptedHint")}</p> : null}
    {mail.problem ? <p className="text-ink">{intl.formatMessage(MAIL_PROBLEM_LABELS[mail.problem])}</p> : null}
    <div className="mt-1 flex flex-wrap gap-2">
      <Button variant="secondary" size="compact" aria-expanded={expanded} onClick={onToggle}>
        {t(expanded ? "hideText" : "showText")}
      </Button>
      {onResend ? <Button id={`listing-application-mail-resend-${mail.id}`} variant="secondary" size="compact"
        onClick={onResend}>{t("resend")}</Button> : null}
    </div>
    {expanded ? <div className="mt-2 space-y-1 border-l border-ink/10 pl-3">
      <p className="font-medium text-ink">{mail.subject}</p>
      <p className="whitespace-pre-wrap text-ink">{mail.body}</p>
    </div> : null}
  </li>;
}
